import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import { 
  CheckCircle2, 
  Sparkles,
  Home,
  AlertCircle,
  ArrowRight,
  Clock
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema
} from '../utils/schemaGenerator';

const DeepCleaningGuidePage = () => {
  const roomGuides = [
    {
      room: "Kitchen",
      tasks: [
        {
          task: "Inside Appliances",
          tip: "Oven, refrigerator, microwave, and dishwasher interiors. Remove racks and shelves to clean thoroughly."
        },
        {
          task: "Cabinets & Drawers",
          tip: "Empty, wipe interiors, and reorganize. Check for expired items and sticky residue."
        },
        {
          task: "Backsplash & Grout",
          tip: "Scrub tile grout with a brush and grout cleaner. Brevard's humidity can cause mildew buildup—address it early."
        },
        {
          task: "Under & Behind Appliances",
          tip: "Pull out the fridge and stove to clean floors and walls. Dust collects fast in Florida homes."
        }
      ]
    },
    {
      room: "Bathrooms",
      tasks: [
        {
          task: "Grout & Tile",
          tip: "Scrub shower and tub grout lines. Use a quality grout cleaner and let it sit before scrubbing."
        },
        {
          task: "Exhaust Fans",
          tip: "Remove vent covers and clean dust buildup. This is especially important in humid climates to prevent mold."
        },
        {
          task: "Behind the Toilet",
          tip: "Clean the wall, baseboards, and floor behind and around the toilet base—often-neglected areas."
        },
        {
          task: "Cabinet Interiors",
          tip: "Wipe down shelves and organize toiletries. Toss expired products."
        }
      ]
    },
    {
      room: "Living & Bedrooms",
      tasks: [
        {
          task: "Ceiling Fans & Light Fixtures",
          tip: "Wipe blades and clean light covers. Dust accumulates quickly in Brevard's climate."
        },
        {
          task: "Baseboards & Trim",
          tip: "Wipe all baseboards, door frames, and window trim throughout the home."
        },
        {
          task: "Windows & Tracks",
          tip: "Clean window glass, sills, and tracks. Coastal homes deal with salt spray and sand—clean regularly."
        },
        {
          task: "Under Furniture",
          tip: "Move beds, dressers, and sofas to vacuum and mop underneath."
        }
      ]
    },
    {
      room: "Often-Forgotten Areas",
      tasks: [
        {
          task: "Air Vents & Returns",
          tip: "Remove vent covers and vacuum or wipe dust from vents and returns."
        },
        {
          task: "Light Switches & Outlet Covers",
          tip: "Wipe down all switches and outlets—high-touch areas that collect fingerprints and grime."
        },
        {
          task: "Door Handles & Knobs",
          tip: "Sanitize all doorknobs, handles, and cabinet pulls."
        },
        {
          task: "Closet Floors & Shelves",
          tip: "Vacuum closet floors and wipe shelves. Organize while you're at it."
        }
      ]
    }
  ];

  const brevardTips = [
    {
      challenge: "Humidity & Mold",
      solution: "Brevard's year-round humidity creates the perfect environment for mold and mildew. When deep cleaning, pay extra attention to bathrooms, kitchen backsplashes, window sills, and anywhere moisture collects. Use mold-preventive cleaners and ensure proper ventilation."
    },
    {
      challenge: "Salt Air (Beachside Homes)",
      solution: "If you live near the coast—Cocoa Beach, Satellite Beach, Indialantic, Indian Harbour Beach, or Melbourne Beach—salt air causes buildup on windows, screens, and outdoor fixtures. Deep clean windows inside and out, and rinse exterior surfaces regularly."
    },
    {
      challenge: "Sand Everywhere",
      solution: "Sand tracks into Brevard homes constantly, especially near the beach. Deep cleaning means pulling out furniture, vacuuming baseboards, and mopping corners where sand hides."
    },
    {
      challenge: "Hurricane Season Prep & Recovery",
      solution: "Before hurricane season (June-November), deep clean to prep your home. After a storm, focus on mold prevention, checking for water intrusion, and cleaning any storm debris or residue."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Comprehensive Deep Cleaning Guide | Pierce Premier",
      description: "Expert deep cleaning guide from Heather's 15+ years of experience. Room-by-room checklists, Brevard County-specific tips, and professional insights.",
      url: "/resources/deep-cleaning-guide"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Resources", url: "/resources" },
      { name: "Deep Cleaning Guide" }
    ]),
    generateLocalBusinessSchema()
  );

  return (
    <>
      <SEO 
        title="Deep Cleaning Guide | Expert Tips | Pierce Premier | Brevard County"
        description="Comprehensive deep cleaning guide for Brevard County homes. Room-by-room checklists, humidity tips, salt air solutions. From Heather's 15+ years of experience."
        canonical="/resources/deep-cleaning-guide"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-5xl">
            <Badge className="bg-indigo-600 text-white mb-4">Expert Guide</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              The Complete Deep Cleaning Guide
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-6">
              From Heather&apos;s 15+ years of executive housekeeping experience, this guide walks you through deep cleaning room by room—with special attention to the unique challenges facing Brevard County homeowners.
            </p>
            <p className="text-base sm:text-lg text-gray-600">
              Whether you&apos;re tackling this yourself or considering professional help, understanding what deep cleaning really involves will help you make informed decisions about your home.
            </p>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              When Should You Deep Clean?
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                "Moving into or out of a home",
                "Before or after major holidays",
                "Spring or fall seasonal refresh",
                "After renovation or construction",
                "Before listing your home for sale",
                "When regular cleaning isn't cutting it",
                "2-4 times per year for maintenance",
                "After extended absences or vacations",
                "When preparing for guests or events"
              ].map((scenario, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <Clock className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{scenario}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Room-by-Room Deep Cleaning Checklist
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                A comprehensive breakdown of what to tackle in every area of your home.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {roomGuides.map((guide, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center">
                        <Home className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{guide.room}</h3>
                    </div>
                    <div className="space-y-4">
                      {guide.tasks.map((item, taskIdx) => (
                        <div key={taskIdx}>
                          <div className="flex items-start gap-2 mb-1">
                            <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                            <span className="font-semibold text-gray-900">{item.task}</span>
                          </div>
                          <p className="text-sm text-gray-600 ml-7">{item.tip}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Deep Cleaning Challenges Unique to Brevard County
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Living in Florida—especially near the coast—presents specific cleaning challenges. Here&apos;s how to address them.
              </p>
            </div>

            <div className="space-y-6">
              {brevardTips.map((tip, idx) => (
                <Card key={idx} className="border-2 border-indigo-100">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <AlertCircle className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{tip.challenge}</h3>
                        <p className="text-gray-700 leading-relaxed">{tip.solution}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-50 to-purple-50">
          <div className="container mx-auto max-w-5xl">
            <Card className="border-4 border-indigo-200 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <Sparkles className="w-12 h-12 text-indigo-600 mb-6 mx-auto" />
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  DIY or Hire a Professional?
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <span className="font-semibold">Deep cleaning is exhausting work.</span> Most homes take 4-8 hours of focused effort—longer if you&apos;re doing it alone or tackling neglected areas. If you have the time, energy, and right tools, DIY deep cleaning is absolutely doable with the guidance above.
                  </p>
                  <p>
                    <span className="font-semibold">However, many homeowners find professional help worth it.</span> At Pierce Premier, Heather&apos;s 15+ years of executive housekeeping experience means we know which products work best, how to tackle tough buildup, and which areas get overlooked. We arrive fully equipped, work efficiently, and deliver results that meet strict standards.
                  </p>
                  <p>
                    <span className="font-semibold">When to consider professional deep cleaning:</span>
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You&apos;re preparing to sell your home</li>
                    <li>Moving in or out and time is limited</li>
                    <li>You&apos;ve fallen behind on maintenance</li>
                    <li>You need it done right the first time</li>
                    <li>Your home has unique challenges (heavy mold, salt buildup, post-construction residue)</li>
                  </ul>
                </div>
                <div className="mt-8 text-center">
                  <Link 
                    to="/services/deep-cleaning"
                    className="inline-flex items-center text-indigo-600 font-semibold hover:text-indigo-700 text-lg"
                  >
                    Learn About Our Deep Cleaning Service
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <CTASection 
          title="Need Professional Help?"
          description="Let Pierce Premier handle the deep cleaning while you focus on everything else. We bring the expertise, tools, and energy to deliver results you'll love."
          primaryButtonText="Request Deep Clean Quote"
        />
      </div>
    </>
  );
};

export default DeepCleaningGuidePage;